import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import { Emitter } from '@patsnap/tahiti-core';
import { HeaderServiceProxy } from '@telecom/shared/services/header-service/header-service-proxy';
import { SubscriptionInfoService } from '@telecom/shared/domain/permission/services/subscription-info-service';
import { PackageInfoService } from '@telecom/shared/domain/permission/services/package-info-service';
import type { SubscriptionListDTO } from '@telecom/shared/domain/permission/apis/stripe-api';
import type { IPackageType } from '@telecom/shared/domain/permission/apis/package-api';
import { UserInfoService, UserLevel } from '../user-info-service';
import { EurekaAmountsControlService } from '../eureka-amounts-control-service';
import { isUserLogin } from '@telecom/shared/startup/user-auth';

/**
 * 用户信息服务实现
 */
@injectable()
export class UserInfoServiceImpl extends UserInfoService {
    private _currentSubscription: SubscriptionListDTO | null = null;
    private _packageList: IPackageType[] = [];
    private _isLoadingSubscription = true;
    private _onSubscriptionChangeEmitter = new Emitter<void>();

    readonly onSubscriptionChange = this._onSubscriptionChangeEmitter.event;

    constructor(
        @inject(HeaderServiceProxy) private readonly _headerServiceProxy: HeaderServiceProxy,
        @inject(SubscriptionInfoService) private readonly _subscriptionInfoService: SubscriptionInfoService,
        @inject(PackageInfoService) private readonly _packageInfoService: PackageInfoService,
        @inject(EurekaAmountsControlService) private readonly _eurekaAmountsControlService: EurekaAmountsControlService,
    ) {
        super();
        // 权益控制（freemium / credit_package）变化会影响用户权益状态，转发为订阅变化事件
        this._eurekaAmountsControlService.onAmountsControlChange(() => {
            this._fireSubscriptionChange();
        });
    }

    isEnterpriseTenant(): boolean {
        return !!this._headerServiceProxy.headerService.account?._info.company_id;
    }

    isEnterpriseCommercialUser(): boolean {
        return this.isEnterpriseTenant()
            && this._packageList.some(item => item.package_type === 'COMMERCIAL');
    }

    isPersonalTenant(): boolean {
        return !this.isEnterpriseTenant();
    }

    isFreeUser(): boolean {
        return this.isPersonalTenant() && this._eurekaAmountsControlService.isFreeUser();
    }

    hasCreditPackage(): boolean {
        return this._eurekaAmountsControlService.hasCreditPackage();
    }

    isPro(): boolean {
        return this.isPersonalTenant() && !this.isFreeUser();
    }

    async userLevel() {
        await this._eurekaAmountsControlService.ensureLoaded();
        const map: Record<UserLevel, boolean> = {
            [UserLevel.Guest]: !(await isUserLogin()),
            [UserLevel.Free]: this.isFreeUser(),
            [UserLevel.Pro]: this.isPro(),
            [UserLevel.Enterprise]: this.isEnterpriseTenant(),
        };

        return Object.keys(map).find(k => map[k as UserLevel]) as UserLevel || UserLevel.Guest;
    }

    getCurrentSubscription(){
        return this._currentSubscription;
    }

    getIpSearchingActivePackage(): IPackageType | null {
        const PRO_PACKAGE_NAMES= ['ip_checking_pro','ip_checking_pro_year'];
        return this._packageList?.find(item =>
            PRO_PACKAGE_NAMES.includes(item.eureka_package)
            && item.package_type === 'COMMERCIAL',
        ) || null;
    }

    isLoadingSubscription(): boolean {
        return this._isLoadingSubscription;
    }

    async syncUserPackageInfo(): Promise<void> {
        // 如果没有登录则不需要调用用户订阅信息
        const isLogin = await isUserLogin();
        if (!isLogin) {
            this._isLoadingSubscription = false;
            this._currentSubscription = null;
            this._fireSubscriptionChange();
            return;
        }

        this._isLoadingSubscription = true;
        this._fireSubscriptionChange();

        // 权益控制（freemium / credit_package）数据用于判断用户权益状态，与订阅信息一并拉取
        void this._eurekaAmountsControlService.ensureLoaded();

        try {
            const [subscription, packageList] = await Promise.all([
                this._subscriptionInfoService.getCurrentSubscription(),
                this._packageInfoService.getPackageInfo(),
            ]);
            this._currentSubscription = subscription;
            this._packageList = packageList;
        } finally {
            this._isLoadingSubscription = false;
            this._fireSubscriptionChange();
        }
    }

    setPackageInfo(packageList: IPackageType[]): void {
        this._packageList = packageList;
        this._fireSubscriptionChange();
    }

    private _fireSubscriptionChange(): void {
        this._onSubscriptionChangeEmitter.fire();
    }
}
